import { useEffect, useState } from 'react';
import { formatPercent, formatRial, type TailReconciliationReport } from '@kia/shared';
import { api } from '../api/client';
import { Card, Chip, Kpi } from '../ui';

/**
 * BIDX-09 — تطبیق دنبالهٔ نرخ‌ها. The per-material drift monitor asks "is THIS bucket behind its last
 * purchase?"; this asks the portfolio question the basket indexation raises: did the index, in aggregate,
 * OVER-follow the tail? It compares — spend-weighted — how far the last basket apply pushed the tail up
 * against how far the tail's confirmed purchases actually moved SINCE, both off the same pre-index base. It
 * also surfaces the DEFLATION RATCHET: the tail indexed up while the driver metals netted down over the same
 * span (the up-only clamp never cuts the tail). Read-only — a down-rebase is a manual group-price edit.
 */
export function TailReconciliationCard() {
  const [report, setReport] = useState<TailReconciliationReport | null>(null);

  useEffect(() => {
    void api<TailReconciliationReport>('/api/prices/tail-reconciliation').then((r) => {
      if (r.ok) setReport(r.body as TailReconciliationReport);
    });
  }, []);

  if (!report) return null;

  const pct = (n: number) => `${n > 0 ? '+' : ''}${formatPercent(n)}`;

  const title = (
    <span style={{ display: 'inline-flex', gap: 10, alignItems: 'center' }}>
      تطبیق دنبالهٔ نرخ‌ها با خرید واقعی
      {report.overFollowPct != null ? (
        <Chip tone={report.overFollowPct > 0.005 ? 'warn' : 'neutral'}>
          {report.overFollowPct > 0.005
            ? `شاخص‌گذاری بیش از خرید ${formatPercent(report.overFollowPct)}`
            : 'شاخص هم‌راستا با خرید'}
        </Chip>
      ) : null}
    </span>
  );

  if (!report.hasApply) {
    return (
      <Card title={title}>
        <p style={{ color: 'var(--ink-3)', lineHeight: 1.9, margin: 0 }}>
          {/* PREPROD-1 — «شاخص‌گذاری سبد»: the apply this card reconciles is stamped with no driver, and the
              factor behind it may have been the dollar, the material average or a manual override just as
              easily as the basket. Name the ACTION («شاخص‌گذاری»), which is true under all five sources. */}
          هنوز شاخص‌گذاری اعمال نشده — چیزی برای تطبیق با خرید واقعی وجود ندارد.
        </p>
      </Card>
    );
  }

  return (
    <Card title={title}>
      <p style={{ margin: '0 0 10px', color: 'var(--ink-2)', lineHeight: 1.9 }}>
        از آخرین اعمال شاخص ({report.lastApplyDate} — {report.spanDays.toLocaleString('fa-IR')} روز
        پیش)، دنبالهٔ نرخ‌ها با ضریب شاخص به‌روزرسانی شد. این کارت آن حرکت را با «قیمت خرید واقعی»
        همان مواد در همین بازه می‌سنجد (هر دو نسبت به قیمت پیش از شاخص) تا سوگیری سامانه‌مند
        بیش/کم‌شاخص‌گذاری دیده شود. فقط نمایشی است؛ اصلاح نرخ از راه ویرایش قیمت گروه انجام می‌شود.
      </p>

      <div className="kpi-grid" style={{ marginBottom: 12 }}>
        <Kpi label="شاخص‌گذاری دنباله" value={pct(report.indexedTailPct)} />
        <Kpi
          label="خرید واقعی دنباله"
          value={report.realizedTailPct != null ? pct(report.realizedTailPct) : '—'}
        />
        <Kpi
          label="بیش‌شاخص‌گذاری (وزنی)"
          value={report.overFollowPct != null ? pct(report.overFollowPct) : '—'}
          tone={report.overFollowPct != null && report.overFollowPct > 0.005 ? 'bad' : 'good'}
        />
        <Kpi
          label="حرکت خالص پیشران‌ها"
          value={report.driverNetPct != null ? pct(report.driverNetPct) : '—'}
        />
      </div>

      {report.ratchet ? (
        <p
          style={{
            margin: '0 0 12px',
            padding: '10px 12px',
            borderRadius: 8,
            background: 'var(--warn-bg, rgba(200,140,0,.12))',
            color: 'var(--ink-2)',
            lineHeight: 1.9,
          }}
        >
          ⚠️ دنباله {pct(report.indexedTailPct)} بالا رفته در حالی‌که فلزات پیشران در همین بازه{' '}
          {report.driverNetPct != null ? pct(report.driverNetPct) : ''} خالص پایین آمده‌اند. چون
          شاخص فقط رو به بالا اعمال می‌شود، دنباله ممکن است بالاتر از بازار مانده باشد — یک
          «بازتنظیم نزولی» دستی روی قیمت گروه را در نظر بگیرید.
        </p>
      ) : null}

      {report.rows.length === 0 ? (
        <p style={{ color: 'var(--ink-3)' }}>
          در این بازه خریدی تأییدشده برای تطبیق با دنبالهٔ شاخص‌گذاری‌شده ثبت نشده است.
        </p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>ماده</th>
                <th>پایهٔ پیش از شاخص</th>
                <th>شاخص‌گذاری</th>
                <th>خرید واقعی</th>
                <th>بیش‌شاخص‌گذاری</th>
                <th>سهم بها</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r) => (
                <tr key={r.code}>
                  <td>
                    {r.name}
                    <span style={{ color: 'var(--ink-3)', fontSize: '.85em' }}>
                      {' '}
                      ({r.code} — {r.group}
                      {r.unit ? `/${r.unit}` : ''})
                    </span>
                  </td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatRial(r.preIndexBook)}
                  </td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {pct(r.indexedMovePct)}
                    <span style={{ color: 'var(--ink-3)', fontSize: '.8em' }}>
                      {' '}
                      → {formatRial(r.indexedBook)}
                    </span>
                  </td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {pct(r.realizedMovePct)}
                    <span style={{ color: 'var(--ink-3)', fontSize: '.8em' }}>
                      {' '}
                      → {formatRial(r.realizedPaid)} ({r.receipts.toLocaleString('fa-IR')})
                    </span>
                  </td>
                  <td
                    style={{
                      fontVariantNumeric: 'tabular-nums',
                      color: r.overFollowPct > 0 ? 'var(--red)' : 'var(--green)',
                      fontWeight: 700,
                    }}
                  >
                    {pct(r.overFollowPct)}
                  </td>
                  <td style={{ fontVariantNumeric: 'tabular-nums' }}>
                    {formatPercent(r.spendShare)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
